import React, { useState, useEffect, Suspense, lazy, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BACKEND_URL } from './services/config';
import { checkAndRefreshToken, isTokenExpired } from './services/auth';
import PreActivation from './components/PreActivation';
import BottomNav from './components/BottomNav';
import AuthPage from './components/AuthPage';
import { ToastProvider } from './components/Toast';
import { LoadingWithTimeout } from './components/LoadingStates';
import { Crown, GraduationCap, Calculator, Menu, X, Globe, Sun, Moon, LogOut, RefreshCw } from 'lucide-react';

// Lazy load tab components
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const PremiumFeatures = lazy(() => import('./components/PremiumFeatures'));
const TradingEducation = lazy(() => import('./components/TradingEducation'));
const RiskCalculator = lazy(() => import('./components/RiskCalculator'));
const TargetTracker = lazy(() => import('./components/TargetTracker'));
const DrawdownAlert = lazy(() => import('./components/DrawdownAlert'));
const CompoundingCalculator = lazy(() => import('./components/CompoundingCalculator'));
const BreakEvenCalculator = lazy(() => import('./components/BreakEvenCalculator'));
const PipValueCalculator = lazy(() => import('./components/PipValueCalculator'));
const MarketSnapshot = lazy(() => import('./components/MarketSnapshot'));
const EconomicCalendar = lazy(() => import('./components/EconomicCalendar'));

// Loading fallback component for lazy loaded components
const LazyLoadFallback = () => (
  <LoadingWithTimeout 
    isLoading={true} 
    loadingText="Memuatkan modul..."
    slowText="Modul agak besar, sila tunggu..."
  >
    <div />
  </LoadingWithTimeout>
);

import ErrorBoundary from './components/ErrorBoundary';

// Lightweight tab loading indicator
const TabFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', opacity: 0.6 }}>
    <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-subtle)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

function AppContent() {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const [isActivated, setIsActivated] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(16750);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('premium');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLightTheme, setIsLightTheme] = useState(() => {
    const saved = localStorage.getItem('ggdesk-theme');
    return saved === 'light';
  });
  const { t, language } = useLanguage();
  
  // Admin panel state - 7 clicks on logo to access (whitelist protected)
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  
  // Refresh key - increments when tab changes to force data refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Force light mode only
  useEffect(() => {
    document.documentElement.classList.remove('dark-mode');
    localStorage.setItem('ggdesk-theme', 'light');
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Increment refreshKey when tab changes to force data refresh
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [activeTab]);

  // Token refresh on app resume/visibility change
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[APP] App became visible, checking token...');
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('[APP] Token expired, logging out...');
          logout();
          return;
        }
        
        // Try to refresh token if expiring soon
        const tokenValid = await checkAndRefreshToken();
        if (!tokenValid) {
          console.log('[APP] Token refresh failed, logging out...');
          logout();
        }
      }
    };
    
    // Check token on mount
    handleVisibilityChange();
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check periodically (every 5 minutes)
    const intervalId = setInterval(handleVisibilityChange, 5 * 60 * 1000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [isAuthenticated, logout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(false);
    setIsActivated(true);
  }, [isAuthenticated]);

  // Redirect to login if not authenticated (MUST be after all hooks)
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Show loading while checking auth
  if (authLoading) {
    return <LazyLoadFallback />;
  }

  const handleActivate = async () => {
    setIsActivated(true);
  };

  const handleTabChange = (tabId) => {
    // Scanner shortcut → go to premium page, auto-select scanner sub-tab
    if (tabId === 'scanner') {
      setActiveTab('premium');
      setSidebarOpen(false);
      // Dispatch custom event for PremiumFeatures to switch to scanner tab
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ggdesk-switch-tab', { detail: 'scanner' }));
      }, 100);
      return;
    }
    const validTabs = ['premium', 'education', 'tools'];
    if (!validTabs.includes(tabId)) {
      tabId = 'premium';
    }
    setActiveTab(tabId);
    setSidebarOpen(false);
  };
  
  // Handle logo click for hidden admin panel (whitelist protected)
  const handleLogoClick = async () => {
    const now = Date.now();
    if (now - lastClickTime > 2000) {
      setLogoClickCount(1);
    } else {
      setLogoClickCount(prev => prev + 1);
    }
    setLastClickTime(now);
    
    if (logoClickCount >= 6) {
      setLogoClickCount(0);
      try {
        const token = localStorage.getItem('ggdesk_auth_token');
        if (!token) return;
        const API_URL = BACKEND_URL;
        const response = await fetch(`${API_URL}/api/admin/whitelist/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.allowed) {
          setShowAdminPanel(true);
        }
      } catch (e) {
        console.log('[Admin] Access check failed');
      }
    }
  };

  // If auth is still loading, show loading spinner
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  if (loading) {
    return (
      <div className="app-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div style={{ 
            width: '50px', 
            height: '50px', 
            border: '3px solid var(--border-subtle)', 
            borderTop: '3px solid #8B1538', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite', 
            margin: '0 auto 20px' 
          }} />
          <div style={{ color: '#8B1538', fontWeight: '600' }}>Loading GG DESK...</div>
        </div>
      </div>
    );
  }
  
  // Show Admin Panel if activated (with lazy loading)
  if (showAdminPanel) {
    return (
      <Suspense fallback={<LazyLoadFallback />}>
        <AdminPanel onBack={() => setShowAdminPanel(false)} />
      </Suspense>
    );
  }

  if (!isActivated) {
    return (
      <div className="app-container">
        <PreActivation onActivate={handleActivate} />
        {/* WhatsApp Float removed - no function */}
      </div>
    );
  }

  const tabs = [
    { id: 'premium', label: 'Premium Signal', icon: Crown, highlight: true },
    { id: 'tools', labelKey: 'menu.tools', icon: Calculator },
    { id: 'education', labelKey: 'menu.education', icon: GraduationCap },
  ];

  return (
    <div className="app-container" style={{ display: 'flex' }}>
      {/* Desktop Sidebar - Always Visible */}
      {!isMobile && (
        <div 
          className="desktop-sidebar"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '260px',
            height: '100vh',
            background: 'var(--bg-card)',
            borderRight: '1px solid var(--border-subtle)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={handleLogoClick}
          data-testid="logo-click-area"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_a04d7c43-2539-4e75-9e44-31d510c5bd81/artifacts/m56e7v94_IMG_2138.jpeg"
              alt="GG DESK Logo"
              style={{ width: '38px', height: '38px', borderRadius: '10px', border: '2px solid #8B1538' }}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#8B1538' }}>GG DESK</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Profit Dollar, Belanja Rupiah</div>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {tabs.map((tab) => {
              const isPremiumItem = tab.id === 'premium';
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  data-testid={`menu-${tab.id}`}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isPremiumItem ? '14px 14px' : '12px 14px',
                    marginBottom: isPremiumItem ? '6px' : '3px',
                    marginTop: isPremiumItem ? '6px' : '0',
                    background: isPremiumItem 
                      ? 'linear-gradient(135deg, #8B1538 0%, #B83A55 100%)'
                      : isActive 
                        ? '#8B1538'
                        : 'transparent',
                    border: 'none',
                    borderRadius: isPremiumItem ? '12px' : '10px',
                    cursor: 'pointer',
                    color: isPremiumItem ? '#FFFFFF' : isActive ? '#FFFFFF' : 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: isPremiumItem ? '700' : isActive ? '600' : '500',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    boxShadow: isPremiumItem 
                      ? '0 4px 15px rgba(128, 0, 32, 0.4)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {isPremiumItem && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'premiumShimmer 2s infinite'
                    }} />
                  )}
                  <tab.icon size={18} style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    filter: isPremiumItem ? 'drop-shadow(0 0 3px rgba(255,255,255,0.5))' : 'none'
                  }} />
                  <span style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    {tab.label || t(tab.labelKey)}
                  </span>
                  {isPremiumItem && (
                    <span style={{
                      background: 'rgba(255,255,255,0.25)',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '8px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      VIP
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div style={{
            padding: '14px 16px',
            borderTop: '1px solid var(--border-subtle)',
          }}>
            {/* User Info */}
            {user && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {user.email}
                </div>
              </div>
            )}
            {/* Logout Button */}
            <button
              onClick={logout}
              data-testid="logout-btn-desktop"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '8px'
              }}
            >
              <LogOut size={16} />
              {language === 'bm' || language === 'ms' ? 'Keluar' : 'Logout'}
            </button>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              textAlign: 'center'
            }}>
              {t('footer.tagline')}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Mobile Sidebar Menu */}
      {isMobile && (
        <div 
          className="sidebar-menu"
          style={{
            position: 'fixed',
            top: 0,
            left: sidebarOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            background: 'var(--bg-card)',
            borderRight: '1px solid var(--border-subtle)',
            zIndex: 999,
            transition: 'left 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: sidebarOpen ? '4px 0 20px rgba(0,0,0,0.15)' : 'none'
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div 
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
              onClick={handleLogoClick}
              data-testid="logo-click-area-mobile"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_a04d7c43-2539-4e75-9e44-31d510c5bd81/artifacts/m56e7v94_IMG_2138.jpeg"
                alt="GG DESK Logo"
                style={{ width: '40px', height: '40px', borderRadius: '10px', border: '2px solid #8B1538' }}
              />
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#8B1538' }}>GG DESK</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Profit Dollar, Belanja Rupiah</div>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: '8px'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Menu Items */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {tabs.map((tab) => {
              const isPremiumItem = tab.id === 'premium';
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: '14px 16px',
                    marginBottom: isPremiumItem ? '8px' : '4px',
                    marginTop: isPremiumItem ? '4px' : '0',
                    background: isPremiumItem 
                      ? 'linear-gradient(135deg, #8B1538 0%, #B83A55 100%)'
                      : isActive 
                        ? '#8B1538' 
                        : 'transparent',
                    border: 'none',
                    borderRadius: isPremiumItem ? '14px' : '12px',
                    cursor: 'pointer',
                    color: isPremiumItem ? '#FFFFFF' : isActive ? '#FFFFFF' : 'var(--text-primary)',
                    fontSize: '15px',
                    fontWeight: isPremiumItem ? '700' : isActive ? '600' : '500',
                    transition: 'all 0.3s ease',
                    textAlign: 'left',
                    boxShadow: isPremiumItem 
                      ? '0 4px 15px rgba(128, 0, 32, 0.4)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {isPremiumItem && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'premiumShimmer 2s infinite'
                    }} />
                  )}
                  <tab.icon size={20} style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    filter: isPremiumItem ? 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' : 'none'
                  }} />
                  <span style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    {tab.label || t(tab.labelKey)}
                  </span>
                  {isPremiumItem && (
                    <span style={{
                      marginLeft: 'auto',
                      background: 'rgba(255,255,255,0.25)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      VIP
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid var(--border-subtle)',
          }}>
            {/* User Info */}
            {user && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {user.email}
                </div>
              </div>
            )}
            {/* Logout Button */}
            <button
              onClick={logout}
              data-testid="logout-btn-mobile"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '8px'
              }}
            >
              <LogOut size={18} />
              {language === 'bm' || language === 'ms' ? 'Keluar' : 'Logout'}
            </button>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-muted)',
              textAlign: 'center'
            }}>
              {t('footer.tagline')}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ 
        marginLeft: isMobile ? 0 : '260px', 
        width: isMobile ? '100%' : 'calc(100% - 260px)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        
        {/* Header Bar - ALWAYS VISIBLE */}
        <header 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            maxWidth: '100vw',
            height: '56px',
            padding: '8px 12px',
            background: 'linear-gradient(135deg, #8B1538 0%, #6E1030 100%)',
            zIndex: 99,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            boxSizing: 'border-box'
          }}
        >
          {/* Hamburger Menu */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                background: 'rgba(255,255,255,0.25)',
                border: 'none',
                borderRadius: '20px',
                padding: '10px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              <Menu size={20} />
            </button>
          )}
          
          {/* Exchange Rate */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '20px'
          }}>
            <span style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>USD / IDR</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#FFFFFF' }}>Rp {exchangeRate.toLocaleString('id-ID')}</span>
          </div>
          
          {/* Spacer */}
          <div style={{ flex: 1 }} />
          
          {/* Refresh Button */}
          <button
            onClick={() => {
              if ('caches' in window) caches.keys().then(n => n.forEach(name => caches.delete(name)));
              window.location.reload();
            }}
            data-testid="refresh-button"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              color: '#FFFFFF',
              padding: '10px 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px'
            }}
          >
            <RefreshCw size={18} />
          </button>
          
          {/* Language Toggle */}
          <button
            onClick={() => {
              const current = localStorage.getItem('ggdesk-language') || 'bm';
              localStorage.setItem('ggdesk-language', current === 'bm' ? 'en' : 'bm');
              window.location.reload();
            }}
            data-testid="language-toggle"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              color: '#FFFFFF',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '600'
            }}
          >
            <Globe size={14} />
            {(localStorage.getItem('ggdesk-language') || 'bm') === 'bm' ? '🇮🇩 ID' : '🇬🇧 EN'}
          </button>
        </header>
        
        <main className="main-content" style={{ paddingBottom: isMobile ? '80px' : '20px', flex: 1 }}>
          <div className="dashboard-container">

          {/* Premium Features Tab */}
          {activeTab === 'premium' && (
            <Suspense fallback={<LazyLoadFallback />}>
              <div className="dashboard-grid">
                <PremiumFeatures key={refreshKey} />
              </div>
            </Suspense>
          )}

          {/* Education Tab */}
          {activeTab === 'education' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              <div style={{ gridColumn: '1 / -1' }}>
                <TradingEducation />
              </div>
            </div>
            </Suspense>
          )}

          {/* Tools Tab */}
          {activeTab === 'tools' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              <RiskCalculator exchangeRate={exchangeRate} />
              <TargetTracker exchangeRate={exchangeRate} />
              <DrawdownAlert />
              <PipValueCalculator exchangeRate={exchangeRate} />
              <CompoundingCalculator exchangeRate={exchangeRate} />
              <BreakEvenCalculator />
              <MarketSnapshot />
              <EconomicCalendar />
            </div>
            </Suspense>
          )}

          {/* Fallback for invalid tabs */}
          {!['premium', 'education', 'tools'].includes(activeTab) && (
            <div className="dashboard-grid" key={refreshKey}>
              <div className="module-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Loading...</h3>
                <button 
                  onClick={() => setActiveTab('premium')}
                  data-testid="goto-premium-btn"
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #8B1538, #B83A55)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Premium Signal
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      </div>
      
      {/* Bottom Navigation - Mobile Only */}
      {isMobile && (
        <BottomNav 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          onMenuClick={() => setSidebarOpen(true)}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <ToastProvider>
              <Routes>
                <Route path="/" element={<AuthPageWrapper />} />
                <Route path="/login" element={<AuthPageWrapper />} />
                <Route path="/reset-password" element={<AuthPageWrapper />} />
                <Route path="/dashboard/*" element={<AppContent />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ToastProvider>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

// Auth Page Wrapper - redirect if already logged in
function AuthPageWrapper() {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LazyLoadFallback />;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <AuthPage />;
}

export default App;
